<script lang="ts">
  import { game } from '$stores/game.svelte';
  import { theme } from '$stores/theme.svelte';
  import { buildArtwork } from '$game/artwork';
  import { shareInvite, shareResult } from '$lib/share';

  let toastMsg = $state('');
  let toastVisible = $state(false);

  const date = new Date()
    .toLocaleString('en-US', { month: 'short', day: 'numeric' })
    .toUpperCase();

  const artworkSvg = $derived.by(() => {
    if (!game.pulse) return '';
    return buildArtwork({
      syncPct: game.syncPct,
      userOrder: game.userOrder.filter((x): x is string => x !== null),
      friendOrder: game.friendOrder,
      matches: game.matches,
      elements: game.pulse.elements,
      size: 220,
      brutal: theme.current === 'brutal'
    });
  });

  function toast(msg: string) {
    toastMsg = msg;
    toastVisible = true;
    setTimeout(() => (toastVisible = false), 1800);
  }

  async function shareTheResult() {
    if (!game.pulse) return;
    const out = await shareResult({
      pulseId: game.pulse.id,
      syncPct: game.syncPct,
      friendHandle: game.friendHandle,
      streak: game.streak
    });
    if (out.ok && out.used === 'clipboard') toast('Link copied — share it');
  }

  async function inviteFriend() {
    const out = await shareInvite();
    if (out.ok && out.used === 'clipboard') toast('Invite link copied');
    else if (out.used === 'noop') toast('Open Bond setup first');
  }
</script>

{#if game.pulse}
  <section class="share-stage">
    <div class="share-card-wrap">
      <div class="share-card">
        <div class="share-bg">
          {#if theme.current === 'brutal'}
            <svg viewBox="0 0 100 178" preserveAspectRatio="none">
              <rect width="100" height="178" fill="#ffffff" />
              <line x1="0" y1="60" x2="100" y2="60" stroke="#000" stroke-width="0.5" />
              <line x1="0" y1="118" x2="100" y2="118" stroke="#000" stroke-width="0.5" />
            </svg>
          {:else}
            <svg viewBox="0 0 100 178" preserveAspectRatio="none">
              <defs>
                <radialGradient id="cardGlow" cx="50%" cy="40%" r="70%">
                  <stop offset="0%" stop-color="rgba(0,212,255,0.18)" />
                  <stop offset="100%" stop-color="rgba(0,0,0,0)" />
                </radialGradient>
                <pattern id="cardGrid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path
                    d="M10 0L0 0L0 10"
                    fill="none"
                    stroke="rgba(0,212,255,0.04)"
                    stroke-width="0.4"
                  />
                </pattern>
              </defs>
              <rect width="100" height="178" fill="#0a0a0a" />
              <rect width="100" height="178" fill="url(#cardGrid)" />
              <rect width="100" height="178" fill="url(#cardGlow)" />
            </svg>
          {/if}
        </div>
        <div class="share-content">
          <div class="share-header">
            <span>VUTWIN · {game.pulse.id} · {game.pulse.categoryLabel}</span>
            <span>{date}</span>
          </div>
          <div class="share-art">
            {@html artworkSvg}
          </div>
          <div class="share-footer">
            <div class="share-pct">{game.syncPct}%</div>
            <div class="share-pct-label">TWIN RATE</div>
            <div class="share-pair">YOU &amp; {game.friendHandle.toUpperCase()}</div>
            <div class="share-streak">🔥 {game.streak} day streak</div>
          </div>
        </div>
        <div class="share-watermark">
          <span class="vu-mark">●</span> VUTWIN.APP <span class="vu-mark">●</span>
        </div>
      </div>
    </div>

    <div class="action-row">
      <button class="btn btn-primary" onclick={shareTheResult}>Share result →</button>
    </div>
    <button class="invite-link" type="button" onclick={inviteFriend}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
        <polyline points="16 6 12 2 8 6" />
        <line x1="12" y1="2" x2="12" y2="15" />
      </svg>
      Invite a friend to bond
    </button>
    <button
      class="share-sb-link"
      onclick={() => {
        game.scoreboardOrigin = 'share';
        game.goto('scoreboard');
      }}
    >
      <span class="sb-live-dot"></span>
      See live scoreboard · who's twinning right now →
    </button>

    {#if toastVisible}
      <div class="toast">{toastMsg}</div>
    {/if}
  </section>
{/if}

<style>
  .share-stage {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 8px var(--gutter) max(var(--safe-cta-bottom), 12px);
    align-items: stretch;
  }
  .share-card-wrap {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
    overflow: hidden;
  }
  .share-card {
    width: 100%;
    max-width: min(340px, 78vw);
    aspect-ratio: 9 / 16;
    max-height: 100%;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    overflow: hidden;
    position: relative;
    box-shadow: var(--shadow-soft);
    display: flex;
    flex-direction: column;
  }
  :global(body.brutal) .share-card {
    border-width: 3px;
    box-shadow: 6px 6px 0 0 var(--text);
  }
  .share-bg {
    position: absolute;
    inset: 0;
    z-index: 0;
  }
  .share-bg :global(svg) {
    width: 100%;
    height: 100%;
  }
  .share-content {
    position: relative;
    z-index: 1;
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 16px;
  }
  .share-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 10px;
    color: var(--text-2);
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 800;
    margin-bottom: 10px;
  }
  .share-art {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 4px 0;
  }
  .share-art :global(svg) {
    width: 90%;
    max-width: 220px;
  }
  .share-footer {
    text-align: center;
    padding-top: 6px;
  }
  .share-pct {
    font-size: 38px;
    font-weight: 900;
    letter-spacing: -0.04em;
    line-height: 1;
    background: linear-gradient(180deg, var(--gold) 0%, var(--hot) 60%, var(--cyan) 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    text-shadow: 0 0 24px rgba(255, 56, 188, 0.35);
    -webkit-font-smoothing: antialiased;
    text-rendering: geometricPrecision;
    paint-order: stroke fill;
  }
  :global(body.brutal) .share-pct {
    background: none;
    color: var(--text);
    -webkit-text-fill-color: var(--text);
  }
  .share-pct-label {
    font-size: 10px;
    color: var(--text-2);
    text-transform: uppercase;
    letter-spacing: 0.15em;
    font-weight: 700;
    margin-top: 2px;
  }
  .share-pair {
    font-size: 13px;
    font-weight: 700;
    margin-top: 8px;
  }
  .share-streak {
    font-size: 11px;
    color: var(--gold);
    margin-top: 4px;
    font-weight: 800;
    text-shadow: 0 0 8px rgba(255, 216, 74, 0.45);
  }
  .share-watermark {
    text-align: center;
    font-size: 9px;
    color: var(--text-3);
    text-transform: uppercase;
    letter-spacing: 0.2em;
    font-weight: 800;
    padding-bottom: 12px;
  }
  .share-watermark .vu-mark {
    color: var(--hot);
  }
  :global(body.brutal) .share-watermark .vu-mark {
    color: var(--text);
  }
  .action-row {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
  }
  /* Layout-only — button visuals come from app.css. */
  .action-row .btn-primary {
    flex: 1;
    text-align: center;
  }
  .invite-link,
  .share-sb-link {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 10px;
    background: transparent;
    border: 1px dashed var(--border);
    border-radius: var(--radius);
    color: var(--text-2);
    font-size: 11px;
    font-weight: 700;
    transition: var(--transition);
    margin-bottom: 6px;
  }
  .invite-link svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
  }
  .invite-link:hover {
    background: var(--surface);
    color: var(--text);
    border-color: var(--primary);
  }
  :global(body.brutal) .invite-link {
    border-style: solid;
    border-width: 2px;
  }
  :global(body.brutal) .share-sb-link {
    border-style: solid;
    border-width: 2px;
  }
  .share-sb-link:hover {
    background: var(--surface);
    color: var(--text);
    border-color: var(--primary);
  }
  .sb-live-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--accent-pink);
    box-shadow: 0 0 8px var(--accent-pink);
    animation: sbDot 1.5s ease-in-out infinite;
  }
  :global(body.brutal) .sb-live-dot {
    background: var(--text);
    border-radius: 0;
    box-shadow: none;
  }
  @keyframes sbDot {
    0%,
    100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.35;
      transform: scale(1.4);
    }
  }
  .toast {
    position: absolute;
    top: 16px;
    left: 50%;
    transform: translateX(-50%);
    padding: 10px 16px;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    font-size: 12px;
    font-weight: 600;
    box-shadow: var(--shadow-soft);
    z-index: 100;
  }
</style>
